import { Link, useNavigate } from 'react-router-dom';
import { Files, Share2, Star, Building2, ScanText, LayoutGrid, List } from 'lucide-react';
import FileTypeIcon from '../components/documents/FileTypeIcon';
import useDocumentActions from '../components/documents/useDocumentActions';
import PinnedBand from '../components/documents/PinnedBand';
import pinService from '../services/pinService';
import EmptyState from '../components/common/EmptyState';
import { TableSkeleton } from '../components/common/LoadingState';
import useAsync from '../hooks/useAsync';
import documentService from '../services/documentService';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../context/PreferencesContext';
import { greeting, firstName, formatNumber, formatBytes, formatRelative } from '../utils/format';
import { shortLevel } from '../utils/permissions';
import cn from '../utils/cn';

function dateline() {
  return new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function StatCard({ icon: Icon, tone, value, label, to }) {
  return (
    <Link to={to} className="stat-card">
      <span className={cn('stat-card__icon', tone && `stat-card__icon--${tone}`)}>
        <Icon size={18} strokeWidth={1.9} />
      </span>
      <span>
        <span className="stat-card__value" style={{ display: 'block' }}>
          {formatNumber(value ?? 0)}
        </span>
        <span className="stat-card__label">{label}</span>
      </span>
    </Link>
  );
}

function SharedPanel({ shares, onOpen }) {
  return (
    <div className="panel panel-list">
      <div className="panel-head">
        <h3>Shared with me</h3>
        <Link to="/shared" className="link">
          View all
        </Link>
      </div>
      {shares.length === 0 && <EmptyState compact icon={Share2} title="Nothing shared yet" body="Documents colleagues share appear here." />}
      {shares.map((s) => (
        <div key={s.id} className="trow" style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }} onClick={() => onOpen(s.document)}>
          <div className="cell-name">
            <FileTypeIcon name={s.document.name} />
            <div style={{ minWidth: 0 }}>
              <div className="title">{s.document.name}</div>
              <div className="sub">
                {s.sharedByUser?.name} · {s.document.department?.name}
              </div>
            </div>
          </div>
          <span className="pill pill-info">{shortLevel(s.capabilities)}</span>
        </div>
      ))}
    </div>
  );
}

function FavoritesPanel({ documents, onOpen }) {
  return (
    <div className="panel panel-list">
      <div className="panel-head">
        <h3>Favorites</h3>
        <Link to="/favorites" className="link">
          View all
        </Link>
      </div>
      {documents.length === 0 && <EmptyState compact icon={Star} title="No favourites yet" body="Star a document to keep it here." />}
      {documents.map((d) => (
        <div key={d.id} className="trow" style={{ gridTemplateColumns: 'minmax(0,1fr)' }} onClick={() => onOpen(d)}>
          <div className="cell-name">
            <FileTypeIcon name={d.name} />
            <div style={{ minWidth: 0 }}>
              <div className="title">{d.name}</div>
              <div className="sub">{d.path}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** v2 dashboard: dateline + greeting, stat cards, recently accessed (list or grid), shared + favourites. */
export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { dashboardLayout, setDashboardLayout } = usePreferences();

  const { data, loading, reload } = useAsync(() => documentService.dashboard(), []);
  const { data: favs } = useAsync(() => documentService.listItems({ scope: 'favorites' }), []);
  const { data: pinned, reload: reloadPins } = useAsync(() => pinService.band(), []);
  const { actions, dialogs } = useDocumentActions({ reload });

  const recent = data?.recentDocuments || [];
  const favDocs = (favs?.items || []).filter((i) => i.type === 'document').slice(0, 4);
  const homeDept = user?.department;

  return (
    <section className="screen screen--wide">
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">{dateline()}</div>
          <h1 style={{ marginTop: 2 }}>
            {greeting()}, {firstName(user?.name || '')}
          </h1>
        </div>
        <div className="page-head__actions">
          <div className="seg" role="group" aria-label="Dashboard layout">
            <button type="button" className={cn(dashboardLayout === 'list' && 'is-active')} onClick={() => setDashboardLayout('list')}>
              <List size={14} style={{ marginRight: 6 }} /> List
            </button>
            <button type="button" className={cn(dashboardLayout === 'grid' && 'is-active')} onClick={() => setDashboardLayout('grid')}>
              <LayoutGrid size={14} style={{ marginRight: 6 }} /> Grid
            </button>
          </div>
          {homeDept && (
            <button type="button" className="btn btn-s" onClick={() => navigate(`/departments/${homeDept.id}`)}>
              Open {homeDept.name} →
            </button>
          )}
        </div>
      </div>

      <div className="grid-stats">
        <StatCard icon={Files} value={data?.stats?.myDocuments} label="My documents" to="/documents" />
        <StatCard icon={Share2} tone="info" value={data?.stats?.sharedWithMe} label="Shared with me" to="/shared" />
        <StatCard icon={Star} tone="warn" value={data?.stats?.favorites} label="Favorites" to="/favorites" />
        <StatCard
          icon={Building2}
          tone="neutral"
          value={data?.stats?.recent}
          label={homeDept ? `${homeDept.name} documents` : 'Recent'}
          to={homeDept ? `/departments/${homeDept.id}` : '/recent'}
        />
      </div>

      {data?.processingCount > 0 && (
        <div className="callout row" style={{ gap: 10 }}>
          <ScanText size={16} />
          <span>
            <strong>{data.processingCount} document(s)</strong> are being processed — text extraction runs in the background.
          </span>
        </div>
      )}

      <PinnedBand
        pins={pinned || []}
        onOpen={(d) => actions.open(d)}
        onUnpin={async (p) => {
          await pinService.unpin(p.id);
          reloadPins();
        }}
      />

      {dashboardLayout === 'list' ? (
        <div className="grid-2">
          <div className="panel panel-list span-all">
            <div className="panel-head">
              <h3>Recently accessed</h3>
              <Link to="/search" className="link">
                Search all
              </Link>
            </div>
            {loading && !data ? (
              <TableSkeleton rows={5} bare />
            ) : recent.length === 0 ? (
              <EmptyState compact icon={Files} title="No documents yet" body="Documents you open will appear here." />
            ) : (
              <>
                <div className="th cols-recent">
                  <span>Name</span>
                  <span>Location</span>
                  <span>Activity</span>
                  <span />
                </div>
                {recent.map((d) => (
                  <div key={d.id} className="trow cols-recent" onClick={() => actions.open(d)}>
                    <div className="cell-name">
                      <FileTypeIcon name={d.name} />
                      <div style={{ minWidth: 0 }}>
                        <div className="title">{d.name}</div>
                        <div className="sub">
                          v{d.currentVersionNo} · {formatBytes(d.sizeBytes)}
                        </div>
                      </div>
                    </div>
                    <div className="muted desktop-only" style={{ fontSize: 13 }}>
                      {d.department?.name}
                      {d.folderName ? ` · ${d.folderName}` : ''}
                    </div>
                    <div className="desktop-only" style={{ fontSize: 13 }}>
                      {d.owner?.name} · {formatRelative(d.updatedAt)}
                    </div>
                    <div className="cell-actions" onClick={(e) => e.stopPropagation()}>
                      {d.capabilities?.includes('share') && (
                        <button type="button" className="link" onClick={() => actions.share(d)}>
                          Share
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          <SharedPanel shares={data?.recentShares || []} onOpen={actions.open} />
          <FavoritesPanel documents={favDocs} onOpen={actions.open} />
        </div>
      ) : (
        <div className="stack" style={{ gap: 20 }}>
          <div>
            <h3 style={{ marginBottom: 10 }}>Recently accessed</h3>
            <div className="grid-cards grid-cards--recent">
              {recent.map((d) => (
                <div key={d.id} className="panel doc-card" onClick={() => actions.open(d)} role="button" tabIndex={0}>
                  <div className="doc-card__top">
                    <FileTypeIcon name={d.name} size="lg" />
                    <span className="muted" style={{ fontSize: 12 }}>
                      v{d.currentVersionNo}
                    </span>
                  </div>
                  <div className="doc-card__name">{d.name}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {d.department?.name}
                    {d.folderName ? ` · ${d.folderName}` : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid-2">
            <SharedPanel shares={data?.recentShares || []} onOpen={actions.open} />
            <FavoritesPanel documents={favDocs} onOpen={actions.open} />
          </div>
        </div>
      )}

      {dialogs}
    </section>
  );
}
